import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  LinkIcon, 
  EnvelopeIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { emailTemplateApi, EmailTemplate } from '../services/emailTemplateApi';

interface ShareRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestToken: string;
  leadName: string;
  leadEmail: string;
  documentTypes: string[];
}

const ShareRequestModal: React.FC<ShareRequestModalProps> = ({
  isOpen,
  onClose,
  requestToken,
  leadName,
  leadEmail,
  documentTypes
}) => {
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const requestUrl = `${window.location.origin}/documents/submit/${requestToken}`;
  
  const [emailSubject, setEmailSubject] = useState(`Document Request - ${documentTypes.join(', ')}`);
  const [emailMessage, setEmailMessage] = useState(`Hello ${leadName},\n\nWe need you to submit the following documents: ${documentTypes.join(', ')}\n\nPlease click the link below to upload your documents:\n${requestUrl}\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your cooperation.\n\nBest regards,\nYour Team`);
  const [error, setError] = useState<string | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  // Fill template with data helper
  const fillTemplateWithData = (template: EmailTemplate) => {
    const filled = emailTemplateApi.fillTemplate(template, {
      leadName,
      leadEmail,
      documentType: documentTypes.join(', '),
      documentUploadLink: requestUrl
    });
    
    setEmailSubject(filled.subject);
    setEmailMessage(filled.body);
  };

  // Load email templates when modal opens
  useEffect(() => {
    const loadEmailTemplates = async () => {
      if (!isOpen) return;
      
      try {
        setLoadingTemplate(true);
        const response = await emailTemplateApi.getDocumentsTemplates();
        
        if (response.success && response.data && response.data.length > 0) {
          setAvailableTemplates(response.data);
          
          // Find the default template or use the first one
          const defaultTemplate = response.data.find(t => t.isDefault) || response.data[0];
          
          if (defaultTemplate) {
            setSelectedTemplate(defaultTemplate);
            fillTemplateWithData(defaultTemplate);
          }
        }
      } catch (err) {
        console.error('Error loading email templates:', err);
        setAvailableTemplates([]);
        setSelectedTemplate(null);
        // If template loading fails, use the default hardcoded values
      } finally {
        setLoadingTemplate(false);
      }
    };

    loadEmailTemplates();
  }, [isOpen, leadName, leadEmail, documentTypes.join(','), requestUrl]);

  // Handle template selection change
  const handleTemplateChange = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    fillTemplateWithData(template);
  };

  if (!isOpen) return null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(requestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      setError(null);

      const response = await import('../services/api').then(module => 
        module.default.post('/documents/requests/send-email', {
          requestToken,
          recipientEmail: leadEmail,
          recipientName: leadName,
          subject: emailSubject,
          message: emailMessage
        })
      );

      if (response.data.success) {
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
          onClose();
        }, 2000);
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Share Document Request</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Request Info */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Request Details</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Lead:</span> {leadName}</p>
              <p><span className="font-medium">Email:</span> {leadEmail}</p>
              <p><span className="font-medium">Documents:</span> {documentTypes.join(', ')}</p>
            </div>
          </div>

          {/* Copy Link Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <LinkIcon className="w-5 h-5 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-900">Copy Link</h4>
            </div>
            <div className="flex space-x-2">
              <input
                type="text"
                value={requestUrl}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50"
              />
              <button
                onClick={handleCopyLink}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-4 h-4 mr-1 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="w-4 h-4 mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Email Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <EnvelopeIcon className="w-5 h-5 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-900">Send via Email</h4>
            </div>
            
            <div className="space-y-3">
              {/* Template Selector */}
              {availableTemplates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Template:
                  </label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = availableTemplates.find(t => t.id === parseInt(e.target.value));
                      if (template) handleTemplateChange(template);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.title} {template.isDefault ? '(Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  rows={6}
                  placeholder="Enter your email message"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || emailSent}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : emailSent ? (
                  <>
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Email Sent!
                  </>
                ) : (
                  <>
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareRequestModal;
